import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button, Form } from 'react-bootstrap';
import UserPointsLedgerModal from './UserPointsLedgerModal';

const ReferralPaymentRequests = () => {
  const [activeTab, setActiveTab] = useState('queue');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [ledgerUser, setLedgerUser] = useState(null);

  const [completeTarget, setCompleteTarget] = useState(null);
  const [completePaymentRef, setCompletePaymentRef] = useState('');
  const [completeNote, setCompleteNote] = useState('');
  const [completeSubmitting, setCompleteSubmitting] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const path =
        activeTab === 'queue'
          ? '/admin/campus/referral-payment-requests'
          : '/admin/campus/referral-payment-requests/history';
      const response = await axios.get(`${API_BASE_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: currentPage, limit: 50 },
      });
      setItems(response.data.items || []);
      setPagination(response.data.pagination || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching referral payment requests:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, currentPage, activeTab]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const refreshBothIfNeeded = () => {
    fetchList();
  };

  const openLedger = (e, row) => {
    e.stopPropagation();
    setLedgerUser({ userId: row.userId, userName: row.userName });
  };

  const submitComplete = async () => {
    if (!completeTarget) return;
    try {
      setCompleteSubmitting(true);
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/campus/referral-payment-requests/${completeTarget.id}/complete`,
        {
          paymentReference: completePaymentRef.trim() || undefined,
          adminNote: completeNote.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCompleteTarget(null);
      setCompletePaymentRef('');
      setCompleteNote('');
      refreshBothIfNeeded();
    } catch (err) {
      console.error(err);
      window.alert(err.response?.data?.error || 'Could not mark as completed.');
    } finally {
      setCompleteSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (!reason) {
      window.alert('Please enter a rejection reason.');
      return;
    }
    try {
      setRejectSubmitting(true);
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/campus/referral-payment-requests/${rejectTarget.id}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRejectTarget(null);
      setRejectReason('');
      refreshBothIfNeeded();
    } catch (err) {
      console.error(err);
      window.alert(err.response?.data?.error || 'Could not reject request.');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const formatPoints = (n) => {
    const v = Math.round(Number(n) || 0);
    return `${v.toLocaleString('en-IN')} pts`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const statusLabel = (s) => {
    if (!s) return '—';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;
    return (
      <div className="d-flex align-items-center justify-content-between border-top px-4 py-3">
        <p className="text-muted small mb-0 d-none d-sm-block">
          Showing{' '}
          <span className="fw-medium text-dark">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
          <span className="fw-medium text-dark">
            {Math.min(pagination.page * pagination.limit, pagination.total)}
          </span>{' '}
          of <span className="fw-medium text-dark">{pagination.total}</span>
        </p>
        <nav aria-label="Pagination">
          <ul className="pagination mb-0">
            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                ‹
              </button>
            </li>
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
              let pageNum;
              if (pagination.totalPages <= 5) {
                pageNum = i + 1;
              } else if (pagination.page <= 3) {
                pageNum = i + 1;
              } else if (pagination.page >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i;
              } else {
                pageNum = pagination.page - 2 + i;
              }
              return (
                <li key={pageNum} className="page-item">
                  <button
                    type="button"
                    className={`page-link ${pagination.page === pageNum ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pagination.page === pageNum
                        ? { backgroundColor: '#4C1D95', borderColor: '#4C1D95', color: '#fff' }
                        : {}
                    }
                  >
                    {pageNum}
                  </button>
                </li>
              );
            })}
            <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                ›
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="mb-2 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>
            Referral payment requests
          </h1>
          <p className="text-muted mb-0 small">
            Points withdrawal queue and history. Click a user to see full referral & withdrawal ledger.
          </p>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'queue' ? 'active fw-semibold' : ''}`}
            onClick={() => handleTabChange('queue')}
          >
            Queue
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'history' ? 'active fw-semibold' : ''}`}
            onClick={() => handleTabChange('history')}
          >
            History
          </button>
        </li>
      </ul>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm border rounded-3">
        <div className="card-header bg-white border-bottom px-4 py-3">
          <h4 className="mb-0 fw-semibold">
            {activeTab === 'queue' ? 'Pending withdrawals' : 'Completed & rejected'}
          </h4>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !items.length ? (
            <div className="alert alert-info m-4" role="alert">
              {activeTab === 'queue'
                ? 'No pending referral payment requests.'
                : 'No completed or rejected withdrawal requests yet.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light" style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">User</th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">Email</th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted text-center">
                      Referrals
                    </th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted text-end">
                      Wallet
                    </th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted text-end">
                      Total withdrawn
                    </th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted text-end">
                      Request
                    </th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">Requested</th>
                    {activeTab === 'history' && (
                      <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">Resolved</th>
                    )}
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">Status</th>
                    <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">Payment</th>
                    {activeTab === 'history' && (
                      <th className="px-4 py-3 fw-semibold text-uppercase small text-muted">Note</th>
                    )}
                    {activeTab === 'queue' && (
                      <th className="px-4 py-3 fw-semibold text-uppercase small text-muted text-end">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="border-top">
                  {items.map((row) => (
                    <tr
                      key={row.id}
                      className="border-bottom"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => openLedger(e, row)}
                    >
                      <td className="px-4 py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                            style={{
                              width: '32px',
                              height: '32px',
                              backgroundColor: '#4C1D95',
                              fontSize: '0.75rem',
                            }}
                          >
                            {getInitials(row.userName)}
                          </div>
                          <div>
                            <div className="fw-medium text-dark">{row.userName}</div>
                            {row.university && (
                              <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                {row.university}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 small text-break" style={{ maxWidth: '200px' }}>
                        {row.userEmail || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="fw-bold text-dark">{row.totalReferrals}</span>
                      </td>
                      <td className="px-4 py-3 text-end text-nowrap">{formatPoints(row.walletBalance)}</td>
                      <td className="px-4 py-3 text-end text-nowrap">
                        {formatPoints(row.totalAmountWithdrawn)}
                      </td>
                      <td className="px-4 py-3 text-end text-nowrap fw-semibold">
                        {formatPoints(row.requestAmount)}
                      </td>
                      <td className="px-4 py-3 small text-muted text-nowrap">
                        {formatDate(row.requestedAt)}
                      </td>
                      {activeTab === 'history' && (
                        <td className="px-4 py-3 small text-muted text-nowrap">
                          {formatDate(row.resolvedAt)}
                        </td>
                      )}
                      <td className="px-4 py-3 small" onClick={(e) => e.stopPropagation()}>
                        <span
                          className="badge rounded-pill"
                          style={{
                            backgroundColor:
                              row.status === 'completed'
                                ? '#dcfce7'
                                : row.status === 'rejected'
                                  ? '#fee2e2'
                                  : row.status === 'processing'
                                    ? '#e0e7ff'
                                    : '#fef3c7',
                            color:
                              row.status === 'completed'
                                ? '#166534'
                                : row.status === 'rejected'
                                  ? '#991b1b'
                                  : row.status === 'processing'
                                    ? '#3730a3'
                                    : '#92400e',
                            fontWeight: 600,
                          }}
                        >
                          {statusLabel(row.status)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 small text-break"
                        style={{ maxWidth: '200px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <code className="text-dark" style={{ fontSize: '0.8rem' }}>
                          {row.paymentOption}
                        </code>
                      </td>
                      {activeTab === 'history' && (
                        <td className="px-4 py-3 small text-muted" style={{ maxWidth: '160px' }}>
                          {row.rejectionReason || '—'}
                        </td>
                      )}
                      {activeTab === 'queue' && (
                        <td className="px-4 py-3 text-end" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-1 justify-content-end flex-wrap">
                            <Button
                              variant="success"
                              size="sm"
                              className="py-0 px-2"
                              onClick={() => setCompleteTarget(row)}
                            >
                              Complete
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="py-0 px-2"
                              onClick={() => setRejectTarget(row)}
                            >
                              Reject
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && renderPagination()}
        </div>
      </div>

      <UserPointsLedgerModal
        show={Boolean(ledgerUser)}
        onHide={() => setLedgerUser(null)}
        userId={ledgerUser?.userId}
        userName={ledgerUser?.userName}
      />

      <Modal
        show={Boolean(completeTarget)}
        onHide={() => setCompleteTarget(null)}
        centered
        size="xl"
        dialogClassName="referral-complete-modal-wide"
      >
        <Modal.Header closeButton>
          <Modal.Title>Mark withdrawal completed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {completeTarget && (
            <div className="text-muted mb-3 border rounded p-3 bg-light">
              <div className="fw-semibold text-dark mb-1">{completeTarget.userName}</div>
              <div className="small">
                {formatPoints(completeTarget.requestAmount)} · {completeTarget.paymentOption}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Payment reference (optional)</Form.Label>
            <Form.Control
              value={completePaymentRef}
              onChange={(e) => setCompletePaymentRef(e.target.value)}
              placeholder="UTR / transaction id"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Internal note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={completeNote}
              onChange={(e) => setCompleteNote(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCompleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="success" onClick={submitComplete} disabled={completeSubmitting}>
            {completeSubmitting ? 'Saving…' : 'Confirm completed'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(rejectTarget)}
        onHide={() => setRejectTarget(null)}
        centered
        size="xl"
        dialogClassName="referral-complete-modal-wide"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject withdrawal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejectTarget && (
            <div className="text-muted mb-3 border rounded p-3 bg-light">
              <p className="mb-2 text-dark">Points will be returned to the user wallet.</p>
              <div className="fw-semibold text-dark mb-1">{rejectTarget.userName}</div>
              <div className="small">{formatPoints(rejectTarget.requestAmount)}</div>
            </div>
          )}
          <Form.Group>
            <Form.Label>Reason (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Visible in admin records / metadata"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submitReject} disabled={rejectSubmitting}>
            {rejectSubmitting ? 'Rejecting…' : 'Reject & refund points'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReferralPaymentRequests;
